import { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TAppDispatch } from 'store/types'
import { ScrollableElementProvider } from 'containers/SubSidebar/AIVideoContent/context'
import { FETCH_LIMIT } from 'containers/SubSidebar/AiLibContent/consts'
import { aiLibThunks } from 'store/features/aiLibVideo/thunks'
import { aiLibSelectors } from 'store/features/aiLibVideo/selectors'
import { NotFound } from './NotFound'
import { useFetchAiLibHistoryOnScroll } from './hooks/useFetchAiLibHistoryOnScroll'
import * as Styled from './styled'
import { IAIVideoProject } from 'types/aiVideo'
import VideoItem from './VideoItem'
import { usePreviewNavigation } from '../hooks/usePreviewNavigation'

const VideosTab: React.FC = () => {
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  )
  const dispatch = useDispatch<TAppDispatch>()
  const scrollableElementRef = useMemo(
    () => ({ current: scrollContainer }),
    [scrollContainer]
  )
  useFetchAiLibHistoryOnScroll(scrollContainer)

  const isInitialLoading = useSelector(aiLibSelectors.isInitialLoading)
  useEffect(() => {
    if (isInitialLoading && scrollContainer) {
      scrollContainer.scrollTop = 0
    }
  }, [isInitialLoading, scrollContainer])

  const historyList = useSelector(aiLibSelectors.historyList)
  const isLoaded = useSelector(aiLibSelectors.isLoaded)

  const {
    activePreviewIndex,
    handleOpenPreview,
    handleClosePreview,
    handleNextPreview,
    handlePrevPreview
  } = usePreviewNavigation(historyList.length)

  useEffect(() => {
    if (!isLoaded) {
      dispatch(aiLibThunks.getHistory({ offset: 0, limit: FETCH_LIMIT }))
    }
  }, [dispatch, isLoaded])

  return (
    <ScrollableElementProvider scrollableElementRef={scrollableElementRef}>
      <Styled.Container>
        <Styled.ItemsContainer ref={setScrollContainer}>
          {historyList.length === 0 && isLoaded ? (
            <NotFound />
          ) : (
            <>
              {historyList.map((item: IAIVideoProject, index: number) => (
                <VideoItem
                  key={item.id}
                  item={item}
                  index={index}
                  isPreviewOpen={activePreviewIndex === index}
                  onOpenPreview={handleOpenPreview}
                  onClosePreview={handleClosePreview}
                  onNext={handleNextPreview}
                  onPrev={handlePrevPreview}
                  hasNext={index < historyList.length - 1}
                  hasPrev={index > 0}
                />
              ))}
            </>
          )}
        </Styled.ItemsContainer>
      </Styled.Container>
    </ScrollableElementProvider>
  )
}

export default memo(VideosTab)
